import React from "react";
import * as Yup from "yup";
import isEqual from "lodash/isEqual";
import { getProfileValues } from "./utils";
import { useQueryClient } from "react-query";
import { useAuth } from "shared/context/auth";
import Validation from "shared/form/Validation";
import { useAuthority } from "context/authority";
import { useCopies } from "shared/hooks/useCopies";
import ContextType from "shared/enums/ContextType";
import { ChangePasswordFieldNameEnum } from "./enums";
import UserFieldsNameEnum from "../user/details/enum";
import { useEntity } from "apps/console/context/entity";
import { getConsoleUserListQueryKey } from "../user/list/dataloader";
import { ProfileComponentProps, UserProfileInitialValues, UserProfileService } from "./types";
import { useGetEventSeriesDetailsList } from "../client/eventSeries/list/dataloader";
import { MAX_JOB_TITLE_LENGTH, MAX_NAME_LENGTH } from "shared/utils/constants";
import { useChangePassword, useGetConsoleUserProfile, useUpdateConsoleUserProfile } from "./dataloader";
import { useStaticData } from "context/staticData";
import LoggedInUserType from "shared/enums/LoggedInUserType";
import SchemaService from "shared/form/renderer/SchemaService";

export default function useUserProfileService(props: ProfileComponentProps): UserProfileService {
  const { userDetail } = useAuth();
  const { clientId } = useEntity();
  const copies = useCopies();
  const queryClient = useQueryClient();
  const [displayPassword, setDisplayPassword] = React.useState<boolean>(false);
  const staticDataObj = useStaticData();

  const userContextId = React.useMemo(() => {
    if (userDetail?.consoleUserRoles?.length) {
      return userDetail?.consoleUserRoles[0].contextId;
    }
    return "";
  }, [userDetail?.consoleUserRoles]);

  const { config } = useAuthority();
  const { data: profile, refetch } = useGetConsoleUserProfile({ consoleUserId: userDetail?.id });
  const { mutateAsync: updateUserProfile } = useUpdateConsoleUserProfile();
  const { mutateAsync: changePassword } = useChangePassword();

  const { data: eventSeriesList = [] } = useGetEventSeriesDetailsList(
    clientId,
    userDetail?.eventSeriesUser ? userDetail?.id : undefined, //event series list should be fetched for only event series user
    config.userType === LoggedInUserType.PLATFORM
  );

  const languageOptions = React.useMemo(() => {
    return SchemaService.getOptionsListFromStaticData(staticDataObj.languageType);
  }, [staticDataObj.languageType]);

  const initialValues = React.useMemo(
    () =>
      ({
        ...getProfileValues(profile),
        [UserFieldsNameEnum.WORK_EMAIL]: profile?.workEmail,
        [ChangePasswordFieldNameEnum.CHANGE_PASSWORD]: false,
        [ChangePasswordFieldNameEnum.PASSWORD_FIELD_PREFIX]: {
          [ChangePasswordFieldNameEnum.CURRENT_PASSWORD]: "",
          [ChangePasswordFieldNameEnum.NEW_PASSWORD]: "",
          [ChangePasswordFieldNameEnum.CONFIRM_PASSWORD]: ""
        }
      } as UserProfileInitialValues),
    [profile]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        [UserFieldsNameEnum.FIRST_NAME]: Validation.textFieldValidation({
          required: true,
          copies: copies,
          max: MAX_NAME_LENGTH,
          min: 0
        }),
        [UserFieldsNameEnum.LAST_NAME]: Validation.textFieldValidation({
          required: true,
          copies: copies,
          max: MAX_NAME_LENGTH,
          min: 0
        }),
        [UserFieldsNameEnum.JOB_TITLE]: Validation.textFieldValidation({
          required: true,
          copies: copies,
          max: MAX_JOB_TITLE_LENGTH,
          min: 0
        }),
        [ChangePasswordFieldNameEnum.PASSWORD_FIELD_PREFIX]: Yup.object().when("changePassword", {
          is: true,
          then: Yup.object().shape({
            [ChangePasswordFieldNameEnum.CURRENT_PASSWORD]: Validation.yupIsReq({
              required: true,
              copies
            }),
            [ChangePasswordFieldNameEnum.NEW_PASSWORD]: Validation.passwordValidation({
              required: true,
              copies
            }),
            [ChangePasswordFieldNameEnum.CONFIRM_PASSWORD]: Validation.yupIsReq({
              required: true,
              copies
            }).test("confirmPassword", copies.get("password.mismatch"), function (value) {
              return this.parent.password === value;
            })
          })
        })
      }),
    [copies]
  );

  const roles = React.useMemo(() => {
    return (
      profile?.userRoles?.map(userRole => {
        if (userRole.contextType === ContextType.PLATFORM) {
          return userRole.role.label;
        } else {
          const eventSeriesName = eventSeriesList?.find(es => es.id === userRole.contextId)?.name;
          return `${userRole.role.label}@${eventSeriesName}`;
        }
      }) || []
    );
  }, [eventSeriesList, profile?.userRoles]);

  const refetchUserList = React.useCallback(() => {
    //To update user list if user is updating profile from user page
    queryClient.invalidateQueries(getConsoleUserListQueryKey(userDetail?.eventSeriesUser ? userContextId : undefined));
  }, [queryClient, userContextId, userDetail?.eventSeriesUser]);

  const updateUserProfileHandler = React.useCallback(
    async (values: UserProfileInitialValues) => {
      const payload = getProfileValues(values);
      if (!isEqual(payload, getProfileValues(profile))) {
        const updatedUser = await updateUserProfile(payload);
        refetch();
        refetchUserList();
        const isLanguageUpdated = updatedUser?.languageType !== profile?.languageType;
        return isLanguageUpdated;
      }
    },
    [profile, updateUserProfile, refetch, refetchUserList]
  );

  const changePasswordHandler = React.useCallback(
    async (values: UserProfileInitialValues) => {
      if (values[ChangePasswordFieldNameEnum.CHANGE_PASSWORD]) {
        await changePassword({
          ...values[ChangePasswordFieldNameEnum.PASSWORD_FIELD_PREFIX],
          id: userDetail?.id ?? ""
        });
      }
    },
    [changePassword, userDetail?.id]
  );

  const submitHandler = React.useCallback(
    async (values: UserProfileInitialValues) => {
      let isLanguageUpdated;
      try {
        isLanguageUpdated = await updateUserProfileHandler(values);
      } catch (err) {}
      try {
        await changePasswordHandler(values);
      } catch (err) {}
      props.closeHandler();
      if (isLanguageUpdated) {
        //language updated, reloading portal to fetch copies
        window.location.reload();
      }
    },
    [changePasswordHandler, props, updateUserProfileHandler]
  );

  return {
    initialValues,
    roles,
    displayPassword,
    validationSchema,
    submitHandler,
    setDisplayPassword,
    languageOptions
  };
}
